import React, { useEffect, useRef, useState } from 'react';
import { BookOpen, Search, Palette, Link as LinkIcon, ArrowRight, Laptop, Quote } from 'lucide-react';

const BookPreviewSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const features = [
    {
      icon: <Palette className="w-6 h-6" />,
      title: "Custom Styling",
      description: "Every preview page is crafted to match your website's aesthetic perfectly",
    },
    {
      icon: <Search className="w-6 h-6" />,
      title: "SEO Optimized",
      description: "Designed to help your book appear in search results organically",
    },
    {
      icon: <LinkIcon className="w-6 h-6" />,
      title: "Interactive Elements",
      description: "Include direct purchase links from within the preview experience",
    },
  ];

  return (
    <section ref={sectionRef} className="relative py-24 lg:py-32 bg-cream-50 overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-[0.02]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%231a1a2e' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
      }} />

      {/* Decorative Elements */}
      <div className="absolute top-20 right-10 opacity-[0.03]">
        <BookOpen className="w-96 h-96 text-ink-900" />
      </div>
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-gold-200/20 rounded-full blur-3xl -translate-x-1/2" />

      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          {/* Left Content */}
          <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
            <div className="inline-flex items-center gap-3 mb-6">
              <span className="w-8 h-px bg-gold-500" />
              <span className="text-gold-600 text-sm font-sans tracking-[0.2em] uppercase">
                Chapter Previews
              </span>
            </div>

            <h2 className="font-display text-4xl md:text-5xl font-bold text-ink-900 mb-6 leading-tight">
              Let Readers Experience
              <span className="block mt-2 text-gradient-gold">Your Words First</span>
            </h2>

            <p className="font-body text-lg text-ink-600 leading-relaxed mb-8">
              Give your audience a taste of your story. Beautifully formatted HTML previews
              create excitement and drive readers to purchase the full book.
            </p>

            {/* Why It Matters Card */}
            <div className="bg-cream-100 border border-cream-300 p-6 mb-8 relative">
              <div className="absolute top-0 left-0 w-12 h-12 border-t-2 border-l-2 border-gold-400" />
              <Quote className="w-8 h-8 text-gold-400 mb-4" />
              <p className="font-accent text-lg text-ink-700 italic">
                "Readers love to try before they buy. A beautifully formatted preview builds
                trust and transforms curious visitors into devoted fans."
              </p>
            </div>

            {/* Features */}
            <div className="space-y-4">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="flex items-start gap-4 p-4 bg-cream-50 border border-cream-200 hover:border-gold-300 transition-colors duration-300"
                >
                  <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center bg-gold-100 text-gold-600 rounded-lg">
                    {feature.icon}
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-semibold text-ink-900 mb-1">
                      {feature.title}
                    </h3>
                    <p className="font-body text-ink-600 text-sm">
                      {feature.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Content - Preview Mockup */}
          <div className={`relative transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
            {/* Decorative Frame */}
            <div className="absolute -inset-4 border border-gold-300/30" />

            {/* Preview Card */}
            <div className="relative bg-cream-100 border border-cream-300 shadow-luxury overflow-hidden">
              {/* Header */}
              <div className="bg-ink-900 px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <BookOpen className="w-5 h-5 text-gold-400" />
                  <span className="font-display text-cream-50">Chapter Preview</span>
                </div>
                <Laptop className="w-5 h-5 text-cream-400" />
              </div>

              {/* Content */}
              <div className="p-8 lg:p-10">
                <h4 className="font-display text-2xl font-bold text-ink-900 mb-2">
                  Chapter One
                </h4>
                <p className="text-gold-600 font-accent italic mb-6">The Beginning</p>

                <div className="space-y-4 font-body text-ink-700 leading-relaxed">
                  <p>
                    The sun dipped below the horizon, painting the sky in hues of amber and
                    crimson. Sarah tightened her scarf against the cool breeze, her heart
                    racing with anticipation.
                  </p>
                  <p>
                    She clutched the weathered journal in her hands, its pages filled with
                    secrets of a life she barely remembered. Each step felt heavier, her
                    resolve tested with every passing moment.
                  </p>
                  <p className="text-ink-500">
                    As she approached the old bridge, a shadow emerged from the fog.
                    "You came," the figure said, voice trembling...
                  </p>
                </div>

                {/* Fade Overlay */}
                <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-cream-100 to-transparent" />

                {/* Continue Reading */}
                <div className="relative mt-8 pt-4 border-t border-cream-300">
                  <span className="font-sans text-sm text-gold-600">Continue reading the full book →</span>
                </div>
              </div>
            </div>

            {/* How It Works Card */}
            <div className="mt-8 p-6 bg-ink-900 border border-ink-800">
              <h3 className="font-display text-lg font-semibold text-cream-50 mb-3">How It Works</h3>
              <p className="font-body text-cream-300 text-sm leading-relaxed">
                We'll take your chosen chapters, format them beautifully for the web,
                and integrate them seamlessly into your site. You choose what to showcase.
              </p>
            </div>
          </div>
        </div>

        {/* CTA Buttons */}
        <div className={`flex flex-col sm:flex-row gap-4 justify-center mt-16 transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <a
            href="/demo-preview"
            className="btn-primary"
          >
            <span className="flex items-center gap-3">
              Explore Demo Preview
              <ArrowRight className="w-5 h-5" />
            </span>
          </a>
          <a
            href="/contact"
            className="btn-secondary"
          >
            <span>Get Started Today</span>
          </a>
        </div>
      </div>
    </section>
  );
};

export default BookPreviewSection;
